import { googleApiKey } from '@/common/loadGoogleMaps';
import axios, { AxiosInstance } from 'axios';

export interface X4bLocation {
	Name: string;
	PlaceName: string;
	Address: string;
	Latitude: number;
	Longitude: number;
}

export interface BoundingBox {
	southWestLatitude: number;
	southWestLongitude: number;
	northEastLatitude: number;
	northEastLongitude: number;
}
// This is translation to TS of flutter code we have in xpress.

/* 1. googleapis package doesn't have places api.
	 2. @googlemaps/js-api-loader kind of works. But the problem is that we need formatted addresses in a list.
			But that package provides only list of place ids. Which we then would need to iterate asyncronously.
	 3. So to replicate legacy functionality let's use raw http api.
*/
export class PlacesRepository {
	private readonly axiosInstance: AxiosInstance;
	private readonly mapsApiKey: string;

	constructor(mapsApiKey: string) {
		this.mapsApiKey = mapsApiKey;
		this.axiosInstance = axios.create();
	}

	get phBoundingBox(): BoundingBox {
		return {
			southWestLatitude: 4.5500,
			southWestLongitude: 119.2500,
			northEastLatitude: 20.4300,
			northEastLongitude: 127.840,
		};
	}

	async searchPlaces(
		query: string,
		options?: {
			latitude?: number;
			longitude?: number;
			boundingBox?: BoundingBox;
		}
	): Promise<X4bLocation[]> {
		const url = 'https://places.googleapis.com/v1/places:searchText';

		const body: Record<string, any> = {
			textQuery: query,
			//"pageSize": 20,
			//"languageCode": "en",
			//"requestedFields": "places.displayName,places.location,places.formattedAddress"
		};

		// locationBias and locationRestriction cannot be used simultaneously
		if (options?.latitude != null && options?.longitude != null && !options?.boundingBox) {
			body.locationBias = {
				circle: {
					center: { latitude: options.latitude, longitude: options.longitude },
					radius: 20000
				}
			};
		}

		if (options?.boundingBox) {
			// Beware, if place name has exact match then it will be returned even if outside of the bounds.
			// E.g. you may try 'london'. See explanation and details here: https://chatgpt.com/share/68405783-3d3c-8009-b0c8-6de2bc067068
			body.locationRestriction = {
				rectangle: {
					low: {
						latitude: options.boundingBox.southWestLatitude,
						longitude: options.boundingBox.southWestLongitude
					},
					high: {
						latitude: options.boundingBox.northEastLatitude,
						longitude: options.boundingBox.northEastLongitude
					}
				}
			};
		}

		try {
			const response = await this.axiosInstance.post(
				url,
				body,
				{
					headers: {
						'Content-Type': 'application/json',
						'X-Goog-Api-Key': this.mapsApiKey,
						'X-Goog-FieldMask': 'places.displayName,places.location,places.formattedAddress'
					}
				}
			);

			if (response.data.places) {
				let locations = response.data.places.map((place: any) => {
					const clean = this.cleanFormattedAddress(place.formattedAddress);
					return {
						PlaceName: place.displayName.text,
						Address: clean,
						Latitude: place.location.latitude,
						Longitude: place.location.longitude,
					};
				});

				// Filter by bounding box if provided
				if (options?.boundingBox) {
					locations = locations.filter((loc: X4bLocation) =>
						loc.Latitude >= options.boundingBox!.southWestLatitude &&
						loc.Latitude <= options.boundingBox!.northEastLatitude &&
						loc.Longitude >= options.boundingBox!.southWestLongitude &&
						loc.Longitude <= options.boundingBox!.northEastLongitude
					);
				}

				return locations;
			}
			return [];
		} catch (error) {
			console.error('Error searching places:', error);
			return [];
		}
	}

	async searchNearby(params: {
		latitude: number;
		longitude: number;
		radiusMeters?: number;
	}): Promise<X4bLocation[]> {
		const url = 'https://places.googleapis.com/v1/places:searchNearby';
		const { latitude, longitude, radiusMeters = 30 } = params;

		const body = {
			maxResultCount: 1,
			locationRestriction: {
				circle: {
					center: {
						latitude,
						longitude,
					},
					radius: radiusMeters
				}
			}
		};

		try {
			const response = await this.axiosInstance.post(
				url,
				body,
				{
					headers: {
						'Content-Type': 'application/json',
						'X-Goog-Api-Key': this.mapsApiKey,
						'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location'
					}
				}
			);

			if (response.data.places?.length) {
				return response.data.places.map((place: any) => {
					const clean = this.cleanFormattedAddress(place.formattedAddress || '');
					return {
						PlaceName: place.displayName?.text || '',
						Address: clean,
						Latitude: place.location.latitude,
						Longitude: place.location.longitude,
					};
				});
			}

			// If nothing is found — fallback to reverse geocoding
			const fallback = await this.reverseGeocode({ latitude, longitude });
			return fallback ? [fallback] : [];
		} catch (error) {
			console.error('Error searching nearby places:', error);
			return [];
		}
	}

	async reverseGeocode(params: {
		latitude: number;
		longitude: number;
	}): Promise<X4bLocation | null> {
		const { latitude, longitude } = params;
		const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${this.mapsApiKey}`;

		try {
			const response = await this.axiosInstance.get(url);

			if (response.data.results?.length) {
				const result = response.data.results[0];
				const address = result.formatted_address || '';
				return {
					Name: '',
					PlaceName: '',
					Address: this.cleanFormattedAddress(address),
					Latitude: latitude,
					Longitude: longitude,
				};
			}
			return null;
		} catch (error) {
			console.error('Error reverse geocoding:', error);
			return null;
		}
	}

	private cleanFormattedAddress(address: string): string {
		const regex = /^[A-Z0-9]{4,7}\+[A-Z0-9]{2,3},?\s*/;
		return address.replace(regex, '');
	}
}

export const placesRepository = new PlacesRepository(googleApiKey);
